/**
 * Play for signed-out players: the headline and subline on the left, the game cluster (an animated
 * pile of the catalog's game icons) on the right, in the space "Your games" takes for signed-in
 * players.
 *
 * Layout rulings:
 * - Wide (@bp-md and up): one row, fixed h-50 (200px) — the signed-in "Your games" section is
 *   ~202px (title row with the h-13 "See all my games" link, gap-3, a card of 130px plus the
 *   carousel's py-1), so guest and signed-in pages line up from "Browse games" down.
 * - Compact: stacked, the visual (h-36) above the text, like the reference collage over its
 *   headline. The DOM keeps the heading first (flex-col-reverse), so screen readers read it first.
 *
 * Games come from the catalog the Browse grid already loaded (pickHeroGames): no requests.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthModal } from '@/contexts/AuthModalContext';
import type { GameProvider } from '@/features/games/data/gameProviders';
import { GameCluster } from './cluster';
import { pickHeroGames } from './pickHeroGames';
import { useStillHero } from './useStillHero';

export interface GuestHeroProps {
  /** The Play catalog (the Browse grid's providers). */
  catalog: readonly GameProvider[];
}

export default function GuestHero({ catalog }: GuestHeroProps) {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthModal();
  const still = useStillHero();
  const games = useMemo(() => pickHeroGames(catalog), [catalog]);
  if (isAuthenticated) return null;

  return (
    <section
      aria-labelledby="home-hero-title"
      data-testid="guest-hero"
      className="flex min-w-0 flex-col-reverse gap-4 @bp-md:h-50 @bp-md:flex-row @bp-md:items-center @bp-md:gap-8"
    >
      <div className="flex min-w-0 flex-col gap-1 @bp-md:flex-1">
        <h1 id="home-hero-title" className="m-0 font-display text-title-lg font-bold text-primary @bp-sm:text-display">
          {t('home.heroTitle', 'One wallet. All your games.')}
        </h1>
        <p className="m-0 font-ui text-body text-secondary">
          {t('home.heroSubtitle', 'Browse everything — sign in only when you add or withdraw funds.')}
        </p>
      </div>
      <div className="relative h-36 min-w-0 @bp-md:h-full @bp-md:flex-1">
        {games.length > 0 ? <GameCluster games={games} still={still} /> : null}
      </div>
    </section>
  );
}
